<template>
    <LoadingContainer :promise="directoryData">
        <div :class="[colClass]">
            <div class="row">
                <div :class="hasRelatedPages || hasOtherVersions ? 'col-md-8' : 'col-12'">
                    <ChildrenList :children-ids="children" :view-only="isViewOnly"></ChildrenList>
                </div>
                <div class="col-md-4" v-if="hasRelatedPages || hasOtherVersions">
                    <!-- eslint-disable-next-line vue/no-unused-vars -->
                    <LoadingContainer :promise="otherVersions" v-slot="{ key, ...data }" v-if="hasOtherVersions">
                        <strong class="d-block mb-1">Other Versions</strong>
                        <ul class="text-smaller">
                            <li v-for="item in data">
                                <a :href="item.url" @click.prevent="gotoOtherPage(item)">{{ item.displayLabel }}</a>
                            </li>
                        </ul>
                    </LoadingContainer>
                    <!-- eslint-disable-next-line vue/no-unused-vars -->
                    <LoadingContainer :promise="relatedPages" v-slot="{ key, ...data }" v-if="hasRelatedPages">
                        <strong class="d-block mb-1">Related</strong>
                        <ul class="text-smaller">
                            <li v-for="item in data">
                                <a :href="item.url" @click.prevent="gotoOtherPage(item)">{{ item.displayLabel }}</a>
                            </li>
                        </ul>
                    </LoadingContainer>
                </div>
            </div>
            <AdminSettingsDifferences :data="data" />
        </div>
    </LoadingContainer>
</template>
<script lang="ts">
import { defineComponent, computed, inject } from "vue";
import { useMainStore } from "@/mainStore";
import { usePageStore } from "../pageState/store";
import { computedAsync } from "@vueuse/core";
import { FormListItem } from "@cs/FormListItem";
import { LandingPageTemplateSettings } from "@cs-ts/landing-page-template-settings";
import type LandingPageTemplateDef from "../elements/LandingPageTemplateDef";
import { props, Props, components, setup } from "./BaseDefMixin.3";
import LoadingContainer from "@/lib/LoadingContainer.3.vue";
import { changePageInjectKey } from "@/AppShell.3.vue.InjectKeys";
export default defineComponent({
    components: {
        ...components,
        LoadingContainer,
    },
    props: props,
    //@ts-ignore
    setup(props: Props<LandingPageTemplateDef, LandingPageTemplateSettings>) {
        const base = setup<LandingPageTemplateDef, LandingPageTemplateSettings>(props);

        const mainStore = useMainStore();

        const directoryData = computed(() => {
            return mainStore
                .getUrl("getDirectoryData")
                .then(url => fetch(url))
                .then(i => i.json() as Promise<{ Pages: FormListItem[]; Tag: string[] }>);
        });
        const pageStore = usePageStore();
        type newPage = {
            areaCode: string;
            formName: string;
            formVersion: string;
            formHash: string;
        };
        const otherVersions = computed(async () => {
            const directoryDataV = await directoryData.value;
            const { formName, formVersion } = pageStore.formData!;
            const cmp = (a: number | string | null | undefined, b: number | string | null | undefined) => (b == null ? (a == null ? 0 : -1) : a == null ? 1 : a < b ? 1 : a > b ? -1 : 0);
            const versionOrderCmp = (a: FormListItem, b: FormListItem) => (a.FormVersionOrder != null || b.FormVersionOrder != null ? cmp(a.FormVersionOrder, b.FormVersionOrder) * -1 : a.FormVersionLabel != null || b.FormVersionLabel != null ? cmp(a.FormVersionLabel, b.FormVersionLabel) * -1 : cmp(a.FormVersion, b.FormVersion) * -1);
            const otherVersions = directoryDataV.Pages.filter((i: FormListItem) => i.FormName == formName && i.FormVersion != formVersion).sort(versionOrderCmp);
            return await Promise.all(
                otherVersions.map(async (pageData: (typeof otherVersions)[0]) => {
                    return {
                        displayLabel: pageData.FormVersionLabel || pageData.FormTitle,
                        url: await mainStore.getUrl("formGoto", {
                            areaCode: pageData.Tenant,
                            formName: pageData.FormName,
                            formVersion: pageData.FormVersion,
                            formHash: pageData.FormHash,
                            page: 0,
                        }),
                        formName: pageData.FormName,
                        formVersion: pageData.FormVersion,
                        formHash: pageData.FormHash,
                        areaCode: pageData.Tenant,
                    } as newPage;
                }),
            );
        });
        const relatedPages = computed(async () => {
            const directoryDataV = await directoryData.value;
            const formSettings = pageStore.formSettings;
            const relatedPageData = (formSettings!.relatedPages || [])
                .map(v => {
                    let match = null as FormListItem | null;
                    directoryDataV.Pages.some((i: FormListItem) => {
                        if (i.FormName == v.formName && i.FormVersion == v.formVersion && (i.Tenant == v.formTenant || v.formTenant == null)) {
                            match = i;
                        }
                        return !!match;
                    });
                    return match ? { pageData: match, relatedData: v } : null!;
                })
                .filter(i => i != null);

            return await Promise.all(
                relatedPageData.map(async ({ pageData, relatedData }) => {
                    return {
                        displayLabel: pageData.FormTitle,
                        url: await mainStore.getUrl("formGoto", {
                            areaCode: pageData.Tenant,
                            formName: pageData.FormName,
                            formVersion: pageData.FormVersion,
                            formHash: pageData.FormHash,
                            page: 0,
                        }),
                        formName: relatedData.formName,
                        formVersion: relatedData.formVersion,
                        areaCode: relatedData.formTenant,
                        formHash: pageData.FormHash,
                    };
                }),
            );
        });
        const hasRelatedPages = computedAsync(async () => {
            return (await relatedPages.value).length > 0;
        });
        const hasOtherVersions = computedAsync(async () => {
            return (await otherVersions.value).length > 0;
        });

        const changePage = inject(changePageInjectKey)!;

        const gotoOtherPage = async (page: newPage) => {
            // TODO duplication of logic?
            //if (page.tenant == (await this.$root.tenantDataPromise).tenantCode) {
            changePage({
                page: "Page",
                settings: {
                    areaCode: page.areaCode,
                    formName: page.formName,
                    formVersion: page.formVersion,
                    formHash: page.formHash,
                    page: 0,
                },
            });
            //} else {
            //    window.location = page.url;
            //}
        };

        return {
            hasRelatedPages,
            hasOtherVersions,
            otherVersions,
            relatedPages,
            gotoOtherPage,
            directoryData,

            ...base.expose,
        };
    },
    //asyncComputed: {
    //},
    //computed: {
    //},
    //methods: {
    //},
});
</script>
